import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Keyboard } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const shortcuts: Shortcut[] = [
  {
    keys: ["⌘", "K"],
    description: "Open command palette / Search",
    category: "Navigation",
  },
  {
    keys: ["⌘", "J"],
    description: "Toggle AI Assistant",
    category: "Interface",
  },
  {
    keys: ["⌘", "B"],
    description: "Toggle Sidebar",
    category: "Interface",
  },
  {
    keys: ["Esc"],
    description: "Close dialog / Command palette",
    category: "Interface",
  },
];

const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
  if (!acc[shortcut.category]) {
    acc[shortcut.category] = [];
  }
  acc[shortcut.category].push(shortcut);
  return acc;
}, {} as Record<string, Shortcut[]>);

const DialogContentComponent = () => (
  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2">
        <Keyboard className="w-5 h-5" />
        Keyboard Shortcuts
      </DialogTitle>
      <DialogDescription>
        Use these keyboard shortcuts to navigate and interact with the app faster.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-6 mt-4">
      {Object.entries(groupedShortcuts).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
            {category}
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Shortcut</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((shortcut, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <kbd
                          key={keyIndex}
                          className="px-2 py-1 text-xs font-semibold text-foreground bg-muted border border-border rounded shadow-sm"
                        >
                          {key}
                        </kbd>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{shortcut.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
      <div className="pt-4 border-t text-sm text-muted-foreground">
        <p className="mb-2">
          <strong>Note:</strong> On Windows/Linux, use <kbd className="px-1.5 py-0.5 text-xs bg-muted border rounded">Ctrl</kbd> instead of{" "}
          <kbd className="px-1.5 py-0.5 text-xs bg-muted border rounded">⌘</kbd> (Command).
        </p>
      </div>
    </div>
  </DialogContent>
);

interface KeyboardShortcutsHelpProps {
  asMenuItem?: boolean;
  /** Classes for the menu row in `asMenuItem` mode — pass `menuRowClass` to sit
   *  in step with the other rows of the profile menu. */
  className?: string;
}

export const KeyboardShortcutsHelp = ({ asMenuItem = false, className }: KeyboardShortcutsHelpProps) => {
  const [open, setOpen] = useState(false);

  if (asMenuItem) {
    return (
      <>
        <DropdownMenuItem 
          onSelect={(e) => {
            e.preventDefault();
            setOpen(true);
          }} 
          className={cn("cursor-pointer", className)}
        >
          <Keyboard className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Keyboard Shortcuts</span>
        </DropdownMenuItem>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContentComponent />
        </Dialog>
      </>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Keyboard className="w-4 h-4" />
          <span className="hidden sm:inline">Shortcuts</span>
        </Button>
      </DialogTrigger>
      <DialogContentComponent />
    </Dialog>
  );
};
